<template>
  <img v-if="barcodeImage" :src="barcodeImage" :alt="alt" />
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import JsBarcode from 'jsbarcode';
import { makeEan13FromMembership } from '@/utils/barcode-utils';
import { useAccountStore } from '@/store';

const accountStore = useAccountStore();

interface Props {
  membershipNumber: string; // raw, may include spaces
  format?: string; // defaults to EAN13
  width?: number;
  height?: number;
  fontSize?: number;
  alt?: string;
}

const props = withDefaults(defineProps<Props>(), {
  membershipNumber: '',
  format: 'EAN13',
  width: 3,
  height: 90,
  fontSize: 18,
  alt: 'Membership Barcode',
});

const barcodeImage = ref<string | null>(null);

const generate = async () => {
  // Compute EAN13 from RACQ-style membership number
  let eanFromApi = await accountStore.getMembershipNumber();
  let ean = null;
  if (eanFromApi && !eanFromApi.data) {
    ean = makeEan13FromMembership(props.membershipNumber);
  } else {
    ean = eanFromApi?.data;
  }
  if (!ean) {
    barcodeImage.value = null;
    return;
  }

  const formattedText = `${ean.slice(0, 1)} ${ean.slice(1, 7)} ${ean.slice(7)}`;
  const canvas = document.createElement('canvas');

  JsBarcode(canvas, ean, {
    format: props.format,
    displayValue: true,
    flat: true,
    font: 'Arial',
    text: formattedText,
    width: props.width,
    height: props.height,
    fontSize: props.fontSize,
  });

  barcodeImage.value = canvas.toDataURL('image/png');
};

onMounted(generate);
watch(() => props.membershipNumber, generate);
</script>
